import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

/**
 * Sistema de logging persistente para producción
 * Guarda logs en AsyncStorage que pueden ser exportados o enviados a un servidor
 */
class ProductionPerformanceLogger {
  constructor() {
    this.enabled = false;
    this.maxLogs = 1000;
    this.flushInterval = 30000; // 30 segundos
    this.logBuffer = [];
    this.init();
  }

  async init() {
    try {
      const enabled = await AsyncStorage.getItem('perf_logger_enabled');
      this.enabled = enabled === 'true';
      
      if (this.enabled) {
        this.startAutoFlush();
      }
    } catch (e) {
      console.error('Error initializing ProductionPerformanceLogger:', e);
    }
  }

  async enable() {
    this.enabled = true;
    await AsyncStorage.setItem('perf_logger_enabled', 'true');
    this.startAutoFlush();
    console.log('✅ Production Performance Logger ENABLED');
  }

  async disable() {
    this.enabled = false;
    await AsyncStorage.setItem('perf_logger_enabled', 'false');
    this.stopAutoFlush();
    console.log('⏸️  Production Performance Logger DISABLED');
  }

  startAutoFlush() {
    if (this.flushTimer) return;
    
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  stopAutoFlush() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Log de eventos de performance
   */
  log(eventName, data = {}) {
    if (!this.enabled) return;

    const entry = {
      timestamp: new Date().toISOString(),
      event: eventName,
      platform: Platform.OS,
      data,
    };

    this.logBuffer.push(entry);

    // Flush si el buffer está lleno
    if (this.logBuffer.length >= 50) {
      this.flush();
    }
  }

  /**
   * Medir tiempo de operación
   */
  async measure(operationName, fn) {
    if (!this.enabled) {
      return await fn();
    }

    const startTime = Date.now();
    let error = null;

    try {
      const result = await fn();
      const duration = Date.now() - startTime;
      
      this.log('operation', {
        name: operationName,
        duration,
        success: true,
      });

      return result;
    } catch (e) {
      const duration = Date.now() - startTime;
      error = e;
      
      this.log('operation', {
        name: operationName,
        duration,
        success: false,
        error: e.message,
      });

      throw e;
    }
  }

  /**
   * Log de errores
   */
  logError(context, error) {
    this.log('error', {
      context,
      message: error.message,
      stack: error.stack,
    });
  }

  /**
   * Log de navegación
   */
  logNavigation(screenName, params = {}, navigationTime = 0) {
    this.log('navigation', {
      screen: screenName,
      params,
      navigationTime,
    });
  }

  /**
   * Log de métricas de performance (FPS, threads, etc)
   */
  logPerformanceMetrics(metrics) {
    this.log('performance_metrics', {
      fps: metrics.fps,
      renderTime: metrics.renderTime,
      jsThreadUsage: metrics.jsThreadUsage,
      uiThreadUsage: metrics.uiThreadUsage,
      slowRenders: metrics.slowRenders,
      jsHeapSize: metrics.jsHeapSize,
      navigationTime: metrics.navigationTime,
      apiResponseTime: metrics.apiResponseTime,
    });
  }

  /**
   * Log de respuesta API
   */
  logAPIResponse(url, duration, status, success = true) {
    this.log('api_response', {
      url: url.length > 100 ? url.substring(0, 100) + '...' : url,
      duration,
      status,
      success,
    });
  }

  /**
   * Log de interacción del usuario
   */
  logUserAction(action, target, data = {}) {
    this.log('user_action', {
      action,
      target,
      ...data,
    });
  }

  /**
   * Guardar buffer en AsyncStorage
   */
  async flush() {
    if (this.logBuffer.length === 0) return;

    try {
      // Cargar logs existentes
      const existingLogsStr = await AsyncStorage.getItem('perf_production_logs');
      const existingLogs = existingLogsStr ? JSON.parse(existingLogsStr) : [];

      // Agregar nuevos logs
      const allLogs = [...existingLogs, ...this.logBuffer];

      // Mantener solo los últimos maxLogs
      const trimmedLogs = allLogs.slice(-this.maxLogs);

      // Guardar
      await AsyncStorage.setItem('perf_production_logs', JSON.stringify(trimmedLogs));

      // Limpiar buffer
      this.logBuffer = [];

      console.log(`💾 Flushed ${this.logBuffer.length} performance logs`);
    } catch (e) {
      console.error('Error flushing logs:', e);
    }
  }

  /**
   * Obtener todos los logs
   */
  async getLogs() {
    try {
      const logsStr = await AsyncStorage.getItem('perf_production_logs');
      return logsStr ? JSON.parse(logsStr) : [];
    } catch (e) {
      console.error('Error getting logs:', e);
      return [];
    }
  }

  /**
   * Limpiar logs
   */
  async clearLogs() {
    try {
      await AsyncStorage.removeItem('perf_production_logs');
      this.logBuffer = [];
      console.log('🗑️  Production logs cleared');
    } catch (e) {
      console.error('Error clearing logs:', e);
    }
  }

  /**
   * Generar reporte
   */
  async generateReport() {
    const logs = await this.getLogs();
    
    // Agrupar por tipo de evento
    const groupedByEvent = logs.reduce((acc, log) => {
      if (!acc[log.event]) acc[log.event] = [];
      acc[log.event].push(log);
      return acc;
    }, {});

    // Calcular estadísticas de operaciones
    const operations = groupedByEvent.operation || [];
    const operationStats = this.calculateOperationStats(operations);

    // Errores
    const errors = groupedByEvent.error || [];

    // Navegación
    const navigation = groupedByEvent.navigation || [];
    const screenVisits = this.calculateScreenVisits(navigation);

    return {
      timestamp: new Date().toISOString(),
      platform: Platform.OS,
      totalEvents: logs.length,
      summary: {
        operations: operations.length,
        errors: errors.length,
        navigations: navigation.length,
        userActions: (groupedByEvent.user_action || []).length,
      },
      operationStats,
      screenVisits,
      recentErrors: errors.slice(-10),
      logs: logs.slice(-100), // Últimos 100 logs
    };
  }

  calculateOperationStats(operations) {
    const stats = {};

    operations.forEach(op => {
      const name = op.data.name;
      if (!stats[name]) {
        stats[name] = {
          count: 0,
          totalDuration: 0,
          successCount: 0,
          errorCount: 0,
          durations: [],
        };
      }

      stats[name].count++;
      stats[name].totalDuration += op.data.duration;
      stats[name].durations.push(op.data.duration);

      if (op.data.success) {
        stats[name].successCount++;
      } else {
        stats[name].errorCount++;
      }
    });

    // Calcular promedios y medianas
    Object.keys(stats).forEach(name => {
      const stat = stats[name];
      stat.avgDuration = Math.round(stat.totalDuration / stat.count);
      stat.minDuration = Math.min(...stat.durations);
      stat.maxDuration = Math.max(...stat.durations);
      
      const sorted = [...stat.durations].sort((a, b) => a - b);
      stat.medianDuration = sorted[Math.floor(sorted.length / 2)];
      
      delete stat.durations; // No incluir en el reporte
    });

    return stats;
  }

  calculateScreenVisits(navigationLogs) {
    const visits = {};

    navigationLogs.forEach(nav => {
      const screen = nav.data.screen;
      if (!visits[screen]) {
        visits[screen] = 0;
      }
      visits[screen]++;
    });

    return visits;
  }

  /**
   * Enviar logs a un servidor (para implementar)
   */
  async sendToServer(serverUrl) {
    try {
      const report = await this.generateReport();

      const response = await fetch(serverUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(report),
      });

      if (response.ok) {
        console.log('✅ Logs sent to server successfully');
        // Opcionalmente limpiar logs después de enviar
        // await this.clearLogs();
        return true;
      } else {
        console.error('❌ Failed to send logs to server:', response.status);
        return false;
      }
    } catch (e) {
      console.error('❌ Error sending logs to server:', e);
      return false;
    }
  }
}

// Singleton
const productionLogger = new ProductionPerformanceLogger();

/**
 * HOC para medir componentes en producción
 */
export const withProductionPerformance = (Component, componentName) => {
  return (props) => {
    const name = componentName || Component.displayName || Component.name;
    const startTime = Date.now();

    React.useEffect(() => {
      const renderTime = Date.now() - startTime;
      productionLogger.log('component_render', {
        component: name,
        renderTime,
      });
    });

    return React.createElement(Component, props);
  };
};

export default productionLogger;
